<template>
  <div id="tosf-hei-programs">
    <div class="row">
      <div class="col">
        <div class="card">
          <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h6 class="mb-0">
              <i class="bi bi-graduation-cap mr-2"></i>Programs & Fee Structure
            </h6>
            <button class="btn btn-sm btn-light" @click="exportList">
              <i class="bi bi-file-earmark-arrow-down mr-1"></i>Export List
            </button>
          </div>
          <div class="card-body">
            <div v-if="heiProfile.programs_data && heiProfile.programs_data.length > 0">
              <div v-for="(program, programIndex) in heiProfile.programs_data" :key="programIndex" class="mb-4">
                <div class="card border-info">
                  <div class="card-header bg-info text-white">
                    <h6 class="mb-0">
                      <i class="bi bi-book mr-2"></i>{{ program.degree_program || `Program ${programIndex + 1}` }}
                    </h6>
                  </div>
                  <div class="card-body">
                    <!-- Program Fee Structure -->
                    <div v-if="program.years && program.years.length > 0" class="table-responsive">
                      <table class="table table-sm table-bordered">
                        <thead class="thead-light">
                          <tr>
                            <th rowspan="2" class="align-middle text-center">Year Level</th>
                            <th rowspan="2" class="align-middle text-center">Semester</th>
                            <th class="text-center">Units</th>
                            <th class="text-center">Tuition/Unit</th>
                            <th class="text-center">Other Fees</th>
                            <th rowspan="2" class="align-middle text-center">Total Fees</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="(year, yearIndex) in program.years">
                            <tr>
                              <td rowspan="2" class="align-middle text-center font-weight-bold">
                                Year {{ yearIndex + 1 }}
                              </td>
                              <td class="text-center">1st Sem</td>
                              <td class="text-center">{{ year.first_sem.units || 0 }}</td>
                              <td class="text-right">₱{{ (year.first_sem.tuition_per_unit || 0).toLocaleString() }}</td>
                              <td class="text-right">₱{{ (year.first_sem.other_fees || 0).toLocaleString() }}</td>
                              <td class="text-center font-weight-bold text-primary">
                                ₱{{ (year.first_sem.total_fees || 0).toLocaleString() }}
                              </td>
                            </tr>
                            <tr>
                              <td class="text-center">2nd Sem</td>
                              <td class="text-center">{{ year.second_sem.units || 0 }}</td>
                              <td class="text-right">₱{{ (year.second_sem.tuition_per_unit || 0).toLocaleString() }}</td>
                              <td class="text-right">₱{{ (year.second_sem.other_fees || 0).toLocaleString() }}</td>
                              <td class="text-center font-weight-bold text-primary">
                                ₱{{ (year.second_sem.total_fees || 0).toLocaleString() }}
                              </td>
                            </tr>
                          </template>
                        </tbody>
                        <tfoot>
                          <tr class="table-info">
                            <td colspan="5" class="text-right font-weight-bold">Program Total:</td>
                            <td class="text-center font-weight-bold text-success">
                              ₱{{ calculateProgramTotal(program).toLocaleString() }}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div v-else class="text-center text-muted">
                      <i class="bi bi-inbox display-4"></i>
                      <p class="mt-2">No fee structure available</p>
                    </div>

                    <!-- OSF Management Section -->
                    <div v-if="program.osf_items && program.osf_items.length > 0" class="mt-4">
                      <div class="card border-success">
                        <div class="card-header bg-success text-white">
                          <h6 class="mb-0">
                            <i class="bi bi-list-check mr-2"></i>Other School Fees (OSF) Management
                          </h6>
                        </div>
                        <div class="card-body">
                          <!-- OSF Items List -->
                          <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                              <thead class="thead-light">
                                <tr>
                                  <th>OSF Item</th>
                                  <th>Default Amount</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="item in program.osf_items" :key="item.id">
                                  <td class="font-weight-bold">{{ item.name }}</td>
                                  <td class="text-right">₱{{ item.amount.toLocaleString() }}</td>
                                  <td>
                                    <span class="badge" :class="item.enabled ? 'badge-success' : 'badge-secondary'">
                                      {{ item.enabled ? 'Enabled' : 'Disabled' }}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- OSF Breakdown Section -->
                    <div v-if="program.osf_items && program.osf_items.length > 0" class="mt-4">
                      <div class="card border-warning">
                        <div class="card-header bg-warning text-dark">
                          <h6 class="mb-0">
                            <i class="bi bi-list-ul mr-2"></i>Other School Fees (OSF) Breakdown
                          </h6>
                        </div>
                        <div class="card-body">
                          <div class="table-responsive">
                            <table class="table table-bordered table-sm osf-breakdown-table">
                              <thead class="thead-light">
                                <tr>
                                  <th>OSF Items</th>
                                  <th v-for="(year, yearIndex) in program.years" :key="yearIndex" colspan="2">
                                    {{ getYearName(yearIndex) }}
                                  </th>
                                </tr>
                                <tr>
                                  <th></th>
                                  <th v-for="(year, yearIndex) in program.years" :key="yearIndex" colspan="2">
                                    <div class="d-flex">
                                      <div class="flex-fill text-center">1st Sem</div>
                                      <div class="flex-fill text-center">2nd Sem</div>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(osfItem, itemIndex) in program.osf_items" :key="osfItem.id">
                                  <td class="font-weight-bold">{{ osfItem.name }}</td>
                                  <template v-for="(year, yearIndex) in program.years">
                                    <td :key="`osf-first-${programIndex}-${yearIndex}-${itemIndex}`" class="text-center">
                                      <div v-if="year.first_sem.osf_items && year.first_sem.osf_items[itemIndex]">
                                        <span v-if="year.first_sem.osf_items[itemIndex].enabled" class="badge badge-success">
                                          ₱{{ year.first_sem.osf_items[itemIndex].amount.toLocaleString() }}
                                        </span>
                                        <span v-else class="text-muted">Disabled</span>
                                      </div>
                                      <span v-else class="text-muted">-</span>
                                    </td>
                                    <td :key="`osf-second-${programIndex}-${yearIndex}-${itemIndex}`" class="text-center">
                                      <div v-if="year.second_sem.osf_items && year.second_sem.osf_items[itemIndex]">
                                        <span v-if="year.second_sem.osf_items[itemIndex].enabled" class="badge badge-success">
                                          ₱{{ year.second_sem.osf_items[itemIndex].amount.toLocaleString() }}
                                        </span>
                                        <span v-else class="text-muted">Disabled</span>
                                      </div>
                                      <span v-else class="text-muted">-</span>
                                    </td>
                                  </template>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted">
              <i class="bi bi-inbox display-4"></i>
              <p class="mt-2">No programs available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'TosfHeiPrograms',
  props: ['heiProfile'],
  methods: {
    calculateProgramTotal(program) {
      if (!program.years) return 0;
      
      let total = 0;
      program.years.forEach(year => {
        const firstSemTotal = parseFloat((year.first_sem && year.first_sem.total_fees) || 0);
        const secondSemTotal = parseFloat((year.second_sem && year.second_sem.total_fees) || 0);
        total += firstSemTotal + secondSemTotal;
      });
      
      return total;
    },

    getYearName(yearIndex) {
      const yearNames = ['First Year', 'Second Year', 'Third Year', 'Fourth Year'];
      return yearNames[yearIndex] || `Year ${yearIndex + 1}`;
    },

    exportList() {
      if (!this.heiProfile.programs_data || this.heiProfile.programs_data.length === 0) {
        toastr.warning('No programs data to export');
        return;
      }

      const wb = XLSX.utils.book_new();
      const usedNames = {};

      this.heiProfile.programs_data.forEach((program, programIndex) => {
        const programName = program.degree_program || `Program ${programIndex + 1}`;

        // Sheet names: strip invalid Excel chars, max 31, unique
        let sheetName = programName.replace(/[:\\\/\?\*\[\]]/g, '').substring(0, 31).trim();
        const baseKey = sheetName;
        if (usedNames[baseKey] !== undefined) {
          usedNames[baseKey]++;
          const suffix = `(${usedNames[baseKey]})`;
          sheetName = sheetName.substring(0, 31 - suffix.length) + suffix;
        } else {
          usedNames[baseKey] = 1;
        }

        const rows = [];

        // --- Fee Structure ---
        rows.push(['Fee Structure']);
        rows.push(['Year', 'Semester', 'Units', 'Tuition Per Unit', 'Other Fees', 'Total Fees']);

        if (program.years) {
          program.years.forEach((year, yearIndex) => {
            rows.push([`Year ${yearIndex + 1}`, '1st Sem', year.first_sem.units || 0, year.first_sem.tuition_per_unit || 0, year.first_sem.other_fees || 0, year.first_sem.total_fees || 0]);
            rows.push([`Year ${yearIndex + 1}`, '2nd Sem', year.second_sem.units || 0, year.second_sem.tuition_per_unit || 0, year.second_sem.other_fees || 0, year.second_sem.total_fees || 0]);
          });
        }

        // --- OSF Management ---
        if (program.osf_items && program.osf_items.length > 0) {
          rows.push([]);
          rows.push(['OSF Management (Other School Fees)']);
          rows.push(['OSF Item', 'Default Amount', 'Status']);

          program.osf_items.forEach(item => {
            rows.push([item.name, item.amount, item.enabled ? 'Enabled' : 'Disabled']);
          });
        }

        const ws = XLSX.utils.aoa_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      const fileName = `hei-programs-${this.heiProfile.hei_code || 'export'}-${this.heiProfile.academic_year || ''}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toastr.success('Programs list exported successfully');
    }
  }
};
</script>

<style scoped>
.border-info {
  border-color: #17a2b8 !important;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table td {
  vertical-align: middle;
}

.table-info {
  background-color: #d1ecf1;
}

.border-success {
  border-color: #28a745 !important;
}

.border-warning {
  border-color: #ffc107 !important;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

/* OSF Breakdown Table Styles */
.osf-breakdown-table th {
  background-color: #e9ecef;
  font-weight: 600;
  font-size: 0.8rem;
  text-align: center;
}

.osf-breakdown-table td {
  font-size: 0.8rem;
  padding: 0.25rem;
}

.flex-fill {
  flex: 1 1 auto;
}
</style>
